import { Amplify } from 'aws-amplify';

/** Auth configuration injected at deploy time via config.js */
export interface AuthConfig {
  userPoolId: string;
  userPoolClientId: string;
  cognitoDomain: string;
  redirectSignIn: string;
  redirectSignOut: string;
}

declare global {
  interface Window {
    __AUTH_CONFIG__?: AuthConfig;
  }
}

export function getConfig(): AuthConfig {
  const cfg = window.__AUTH_CONFIG__;
  if (!cfg) {
    throw new Error('Auth config not loaded. Ensure config.js is included.');
  }
  return cfg;
}

const WAIT_INTERVAL_MS = 50;
const WAIT_TIMEOUT_MS = 5000;

/**
 * Wait for window.__AUTH_CONFIG__ to become available.
 * config.js is loaded with `async` so it may not be ready when JS executes.
 */
export function waitForConfig(): Promise<AuthConfig> {
  const immediate = window.__AUTH_CONFIG__;
  if (immediate) {
    return Promise.resolve(immediate);
  }
  return new Promise((resolve, reject) => {
    const deadline = Date.now() + WAIT_TIMEOUT_MS;
    const id = setInterval(() => {
      const cfg = window.__AUTH_CONFIG__;
      if (cfg) {
        clearInterval(id);
        resolve(cfg);
      } else if (Date.now() >= deadline) {
        clearInterval(id);
        reject(
          new Error('Auth config not loaded. Ensure config.js is included.')
        );
      }
    }, WAIT_INTERVAL_MS);
  });
}

let amplifyConfigured = false;

/** Call Amplify.configure() exactly once using window.__AUTH_CONFIG__. */
export function ensureAmplifyConfigured(): void {
  if (amplifyConfigured) {
    return;
  }
  const config = getConfig();
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: config.userPoolId,
        userPoolClientId: config.userPoolClientId,
        loginWith: {
          oauth: {
            domain: config.cognitoDomain,
            scopes: ['openid', 'email', 'profile'],
            redirectSignIn: [config.redirectSignIn],
            redirectSignOut: [config.redirectSignOut],
            responseType: 'code',
          },
        },
      },
    },
  });
  amplifyConfigured = true;
}

/** Wait for config.js to load, then call Amplify.configure() exactly once. */
export async function waitForAmplifyConfigured(): Promise<void> {
  if (amplifyConfigured) {
    return;
  }
  await waitForConfig();
  ensureAmplifyConfigured();
}
